import React from "react";
import {
  ConfigurationFormActions,
  ConfigurationSyncSummary,
  useConfigurationEditor,
} from "./CustomAiConfigurationForm";

const CODEX_MODELS = Object.freeze([
  "gpt-6-astra",
  "gpt-6-sol",
  "gpt-6-luna",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-5.5",
  "gpt-5.4",
  "gpt-5.4-mini",
]);
const REASONING_EFFORTS = Object.freeze(["none", "low", "medium", "high", "xhigh", "max"]);

function ModelOptions({ current }) {
  const models = CODEX_MODELS.includes(current) ? CODEX_MODELS : [current, ...CODEX_MODELS];
  return models.map((model) => <option key={model} value={model}>{model}</option>);
}

function NumberField({ label, description, value, min, max, onChange }) {
  return (
    <label className="adminFormField">
      <span>{label}</span>
      <input type="number" aria-label={label} min={min} max={max} step="1" required value={value}
        onChange={(event) => onChange(Number(event.target.value))} />
      <small>{description}</small>
    </label>
  );
}

export default function CodexConfigurationForm(props) {
  const editor = useConfigurationEditor({ ...props, documentKey: "codex", label: "Codex" });
  const { draft, setDraft } = editor;
  const setField = (field, value) => setDraft({ ...draft, [field]: value });
  const setTarget = (target, checked) => {
    const next = checked
      ? [...draft.enabledTargetTypes, target]
      : draft.enabledTargetTypes.filter((value) => value !== target);
    setField("enabledTargetTypes", ["SETUP", "MOVE_STOP"].filter((value) => next.includes(value)));
  };

  return (
    <form className="adminConfigurationForm" onSubmit={(event) => { event.preventDefault(); editor.save(); }}>
      <ConfigurationSyncSummary envelope={editor.envelope} syncStatus={props.syncStatus}
        enabled={editor.envelope.configuration.enabled} />
      <fieldset className="adminFieldset" disabled={editor.saving}>
        <legend>Codex process</legend>
        <label className="adminCheckboxField">
          <input type="checkbox" aria-label="Enable Codex evaluations" checked={draft.enabled}
            onChange={(event) => setField("enabled", event.target.checked)} />
          <span><strong>Enable Codex evaluations</strong><small>Starts processing enabled automatic targets and manual chart requests.</small></span>
        </label>
        <div className="adminFieldGroup" role="group" aria-label="Automatic Codex target types">
          <p>Automatic target types</p>
          <label className="adminCheckboxField">
            <input type="checkbox" aria-label="Automatically evaluate setup events" checked={draft.enabledTargetTypes.includes("SETUP")}
              onChange={(event) => setTarget("SETUP", event.target.checked)} />
            <span><strong>Automatically evaluate setup events</strong><small>Confirmed SETUP events enter the automatic queue.</small></span>
          </label>
          <label className="adminCheckboxField">
            <input type="checkbox" aria-label="Automatically evaluate move-stop events" checked={draft.enabledTargetTypes.includes("MOVE_STOP")}
              onChange={(event) => setTarget("MOVE_STOP", event.target.checked)} />
            <span><strong>Automatically evaluate move-stop events</strong><small>Confirmed MOVE_STOP events enter the automatic queue.</small></span>
          </label>
        </div>
        <label className="adminCheckboxField">
          <input type="checkbox" aria-label="Include chart screenshots" checked={draft.screenshotsEnabled}
            onChange={(event) => setField("screenshotsEnabled", event.target.checked)} />
          <span><strong>Include chart screenshots</strong><small>Master switch for automatic visual evaluations.</small></span>
        </label>
      </fieldset>

      <fieldset className="adminFieldset adminFormGrid" disabled={editor.saving}>
        <legend>Models and execution</legend>
        <label className="adminFormField">
          <span>Automatic Codex model</span>
          <select aria-label="Automatic Codex model" value={draft.automaticModel} onChange={(event) => setField("automaticModel", event.target.value)}>
            <ModelOptions current={draft.automaticModel} />
          </select>
          <small>Used for automatic setup and move-stop evaluations.</small>
        </label>
        <label className="adminFormField">
          <span>Manual Codex model</span>
          <select aria-label="Manual Codex model" value={draft.manualModel} onChange={(event) => setField("manualModel", event.target.value)}>
            <ModelOptions current={draft.manualModel} />
          </select>
          <small>Default for manually requested chart evaluations.</small>
        </label>
        <label className="adminFormField">
          <span>Reasoning effort</span>
          <select aria-label="Reasoning effort" value={draft.reasoningEffort} onChange={(event) => setField("reasoningEffort", event.target.value)}>
            {REASONING_EFFORTS.map((effort) => <option key={effort} value={effort}>{effort}</option>)}
          </select>
          <small>Reasoning used by automatic evaluations; manual chart evaluation may apply its own low-effort profile.</small>
        </label>
        <NumberField label="Evaluation timeout in seconds" value={draft.timeoutSeconds} min="60" max="900"
          description="Terminate unfinished work after 60–900 seconds." onChange={(value) => setField("timeoutSeconds", value)} />
        <NumberField label="Recent symbol events used as context" value={draft.eventContextLimit} min="0" max="10"
          description="Number of recent symbol events supplied as context. Use 0 for none." onChange={(value) => setField("eventContextLimit", value)} />
        <NumberField label="Automatic evaluations per UTC day" value={draft.automaticDailyLimit} min="0" max="2147483647"
          description="Daily automatic safety limit. Use 0 to disable automatic requests." onChange={(value) => setField("automaticDailyLimit", value)} />
        <NumberField label="Manual evaluations per UTC day" value={draft.manualDailyLimit} min="0" max="2147483647"
          description="Daily manual safety limit. Use 0 to block manual requests." onChange={(value) => setField("manualDailyLimit", value)} />
      </fieldset>
      <p className="adminFieldHint">Shared assessment capacity is configured on the Workers tab.</p>
      <ConfigurationFormActions editor={editor} label="Codex" onRefresh={props.onRefresh} />
    </form>
  );
}
